import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import PasswordInput from "../components/PasswordInput";
import GoogleSignInButton, {
  isGoogleSignInAvailable,
} from "../components/GoogleSignInButton";
import AppleSignInButton, {
  isAppleSignInAvailable,
} from "../components/AppleSignInButton";
import { markaAdi, markaLogosu } from "../lib/marka";

export default function Login() {
  const { t, lang } = useI18n();
  const isTr = lang === "tr";
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/dashboard");
    } catch (err) {
      // Sunucu hatasını "şifren yanlış" gibi göstermeyi bırak.
      //
      // 25 Tem 2026: backend belleği aşıp yeniden başlarken 502 dönüyordu.
      // 502 cevabında JSON gövdesi olmadığı için `detail` boş kalıyor ve bu
      // satır yedeğe düşüp "geçersiz e-posta veya şifre" yazıyordu. Kullanıcı
      // hesabının bozuk olduğunu sanıp defalarca deniyordu. Gerçek sebep
      // sunucunun ayakta olmamasıydı.
      //
      // 401  → kimlik bilgileri gerçekten hatalı
      // yanıt yok → ağ/bağlantı sorunu
      // diğer (5xx) → sunucu tarafı, tekrar denemek işe yarar
      const st = err?.response?.status;
      const msg =
        st === 401
          ? (err?.response?.data?.detail || t("auth.invalid"))
          : !st
          ? (isTr ? "Bağlantı kurulamadı. İnternetini kontrol edip tekrar dene." : "Could not connect. Check your connection and try again.")
          : (isTr ? "Sunucuya şu an ulaşılamıyor. Birazdan tekrar dene." : "The server is unavailable right now. Please try again shortly.");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-6 py-12 bg-stone-50">
      <div className="w-full max-w-md bg-white border border-stone-200 rounded-3xl p-8 shadow-sm" data-testid="login-card">
        <Link to="/" className="flex items-center gap-2 mb-6">
          {/* Marka logosu — üst menüdekiyle aynı görsel (geçici lucide kedi
              ikonu yerine). Giriş, markayla ilk karşılaşma anı. */}
          <span className="w-10 h-10 rounded-xl overflow-hidden shadow-sm">
            <img src={markaLogosu(lang)} alt={markaAdi(lang)} className="w-full h-full object-cover" />
          </span>
          {/* 🔴 11 EYLÜL — MARKA `lib/marka.js`TEN. Burada ad `t("appName")`,
              logo da `lang === "tr"` ile SEÇİLİYORDU: Türkiye'de İngilizceye
              geçen kullanıcı başlıkta "StrayCats" görürken bu kartta
              "StrayPaw" + pati ikonu görüyordu. Kullanıcı cihazda yakaladı:
              "giriş sayfamızdaki logomuz da StrayPaw olarak kalmış."
              Aynı karar dört ekranda dört kez yazılmıştı; artık tek yerde. */}
          <div className="font-display font-bold">{markaAdi(lang)}</div>
        </Link>
        <h1 className="font-display font-bold text-3xl tracking-tight">{t("auth.loginTitle")}</h1>

        {/* Google ve Apple formun ÜSTÜNDE. Yaralı kedinin başında duran kimse
            şifre uydurmaz — uygulamayı kapatır. E-posta formu, tercih edenler
            için aşağıda duruyor.

            Apple düğmesi yalnız iOS uygulamasında, Google ise web + Android +
            (istemci kimliği varsa) iOS'ta çizilir. Ayraç ("veya") ikisinden en
            az biri çizilmiyorsa gösterilmez; yoksa hiçliği formdan ayıran bir
            çizgi kalır. */}
        {(isGoogleSignInAvailable() || isAppleSignInAvailable()) && (
          <>
            {/* Apple ÖNCE. Apple'ın kuralı, kendi giriş seçeneğinin diğerlerinden
                daha az göze çarpmamasını istiyor; en üst sıra bu şartı
                tartışmasız karşılar. Web ve Android'de bu düğme zaten
                çizilmediği için oralarda Google en üstte kalır. */}
            <div className="mt-6 space-y-2.5">
              <AppleSignInButton
                isTr={isTr}
                onSuccess={() => {
                  toast.success(lang === "tr" ? "Hoş geldin!" : "Welcome!");
                  nav("/dashboard");
                }}
              />
              {isGoogleSignInAvailable() && (
                <GoogleSignInButton
                  isTr={isTr}
                  onSuccess={() => {
                    toast.success(lang === "tr" ? "Hoş geldin!" : "Welcome!");
                    nav("/dashboard");
                  }}
                />
              )}
            </div>

            <div className="flex items-center gap-3 my-5">
              <span className="h-px flex-1 bg-stone-200" />
              <span className="text-xs text-stone-400 font-medium">
                {lang === "tr" ? "veya" : "or"}
              </span>
              <span className="h-px flex-1 bg-stone-200" />
            </div>
          </>
        )}

        <form onSubmit={submit} className="space-y-4 mt-6">
          <div className="space-y-1.5">
            <Label htmlFor="email">{t("auth.email")}</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="rounded-xl"
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">{t("auth.password")}</Label>
              <Link
                to="/forgot-password"
                className="text-xs text-stone-500 hover:text-orange-700"
                data-testid="login-forgot-link"
              >
                {t("auth.forgotPassword")}
              </Link>
            </div>
            <PasswordInput
              id="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="rounded-xl"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-orange-600 hover:bg-orange-700 gap-2"
            data-testid="login-submit-btn"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {t("auth.submitLogin")}
          </Button>
        </form>
        <div className="mt-4 text-sm text-stone-500 text-center">
          <Link to="/register" className="hover:text-orange-700" data-testid="switch-register-link">
            {t("auth.switchToRegister")}
          </Link>
        </div>
      </div>
    </div>
  );
}
